import { ARENA } from '../../../config/constants.js';

/**
 * The Levy — The Tithe's signature (Act III-3, ACT3-BIBLE §3).
 *
 * The boss is **immune** until the player pays into a collection ring at the
 * arena's centre. Payment is a resource you own and would rather keep; paying
 * opens a vulnerability window scaled to what you gave. Nothing else in the
 * game has ever spent the *player's* resources as a combat verb — the Famished
 * Court has an economy, but it is the boss's own.
 *
 * This is a system rather than an AttackPattern because the rule has to hold
 * for the whole fight, not for whichever six seconds the scheduler happened to
 * pick it. That's the same call data/act2Bosses.js made for `gloaming` and
 * `ballast`: a fight-long texture declared on the boss def, owned by a small
 * system, rendered through one visual.
 *
 * ACT3-BIBLE §3's guard rails are the reason this is the act's highest
 * save-integrity risk and they are not optional:
 *
 *  - the max-HP levy floors at `maxHpFloor` of base, so no sequence of levies
 *    can whittle a run down to unwinnable;
 *  - nothing here touches the *save* — every cost is in-run state (ultimate
 *    charge, current max HP, banked XP), so a levy can cost a run and can never
 *    cost a profile;
 *  - a levy with nothing payable is **refused, not defaulted**: the boss simply
 *    takes a free heal instead of taking something the player can't afford.
 *
 * `Arrears`: stall past `arrearsSeconds` and the collector takes the payment
 * itself, at the worst available rate and with no window opened.
 */
export class LevySystem {
  constructor(eventBus, config, { player, progression, ultimate }) {
    this.eventBus = eventBus;
    this.config = config;
    this.player = player;
    this.progression = progression;
    this.ultimate = ultimate;
    this.boss = null;

    this._baseMaxHp = player.maxHp;
    this._levied = 0; // levies paid this phase
    this._phase = 0;
    this._window = 0; // seconds of vulnerability left
    this._stall = 0; // seconds this levy has gone unpaid
    this._offered = false;

    this._onPay = ({ tier }) => this._pay(tier);
    eventBus.on('levy:pay', this._onPay);
  }

  get ringX() { return ARENA.x + ARENA.width / 2; }
  get ringY() { return ARENA.y + ARENA.height / 2; }

  /** True while the player is standing in the collection ring. */
  get playerAtRing() {
    return Math.hypot(this.player.x - this.ringX, this.player.y - this.ringY) < this.config.ringRadius;
  }

  /** The costs the player can actually afford right now, best-value first. */
  affordableTiers() {
    const tiers = [];
    if (this.ultimate?.charge >= 1) {
      tiers.push({ id: 'ultimate', label: 'Your charged ultimate', window: this.config.windowSeconds[2] });
    }
    if (this.player.maxHp > this._baseMaxHp * this.config.maxHpFloor) {
      tiers.push({ id: 'maxHp', label: `${Math.round(this.config.maxHpCost * 100)}% of your max HP`, window: this.config.windowSeconds[1] });
    }
    if ((this.progression?.xp ?? 0) > 0) {
      tiers.push({ id: 'xp', label: 'Your progress toward the next level', window: this.config.windowSeconds[0] });
    }
    return tiers;
  }

  update(dt, boss) {
    this.boss = boss;
    if (!boss || boss.isDying) return;

    if (boss.phase !== this._phase) {
      this._phase = boss.phase;
      this._levied = 0;
    }

    if (this._window > 0) {
      this._window -= dt;
      if (this._window <= 0) this._closeWindow();
      boss.invulnerable = false;
      return;
    }

    // Every levy for this phase has been paid: the boss stays hittable until
    // the next phase resets the ledger. A fight where the price only ever went
    // up and never let go would be a tax, not a mechanic.
    if (this._levied >= this.config.leviesPerPhase) {
      boss.invulnerable = false;
      return;
    }

    boss.invulnerable = true;
    if (!this._offered) {
      this._offered = true;
      this._stall = 0;
      this.eventBus.emit('levy:offer', { tiers: this.affordableTiers() });
    }

    this._stall += dt;
    if (this._stall >= this.config.arrearsSeconds) this._arrears();
  }

  /** Pays a tier by id (or the cheapest affordable one if the id is stale). */
  _pay(tierId) {
    if (!this._offered || this._window > 0) return;
    const tiers = this.affordableTiers();
    const tier = tiers.find((t) => t.id === tierId);
    if (!tier) return;

    if (tier.id === 'ultimate') this.ultimate.charge = 0;
    if (tier.id === 'maxHp') {
      // Floors at maxHpFloor of the run's starting max HP — the guard rail
      // that keeps a hoarder's punishment short of an unwinnable run.
      const target = Math.max(this._baseMaxHp * this.config.maxHpFloor, this.player.maxHp * (1 - this.config.maxHpCost));
      this.player.maxHp = target;
      this.player.hp = Math.min(this.player.hp, this.player.maxHp);
    }
    if (tier.id === 'xp') this.progression.xp = 0;

    this._openWindow(tier.window, tier.label);
  }

  /**
   * Arrears: stall past the timer and the collector helps itself, at the worst
   * available rate — the cost is taken and NO window opens.
   */
  _arrears() {
    const tiers = this.affordableTiers();
    if (!tiers.length) {
      // Nothing payable: refused, not defaulted. The boss takes a heal instead,
      // which costs the player time rather than a resource they don't have.
      this._levied++;
      this._offered = false;
      this._stall = 0;
      if (this.boss) {
        this.boss.hp = Math.min(this.boss.maxHp, this.boss.hp + this.boss.maxHp * this.config.refusalHeal);
      }
      this.eventBus.emit('levy:refused');
      return;
    }
    const worst = tiers[tiers.length - 1];
    this._pay(worst.id);
    // _pay opened a window sized for a willing payment; arrears is not a
    // willing payment, so it is cut to the shortest tier.
    this._window = Math.min(this._window, this.config.windowSeconds[0] * 0.5);
    this.eventBus.emit('levy:arrears', { label: worst.label });
  }

  _openWindow(seconds, label) {
    this._levied++;
    this._offered = false;
    this._stall = 0;
    this._window = seconds;
    if (this.boss) this.boss.invulnerable = false;
    this.eventBus.emit('levy:paid', { seconds, label });
  }

  _closeWindow() {
    this._window = 0;
    this.eventBus.emit('levy:closed');
  }

  getVisual() {
    return {
      x: this.ringX,
      y: this.ringY,
      radius: this.config.ringRadius,
      // The three states the ring has to read as at a glance: collecting
      // (immune, pay me), open (hit it), and dormant (this phase is paid).
      state: this._window > 0 ? 'open' : (this._offered ? 'collecting' : 'idle'),
      urgency: Math.min(1, this._stall / this.config.arrearsSeconds),
      windowRemaining: this._window,
    };
  }

  dispose() {
    this.eventBus.off('levy:pay', this._onPay);
    if (this.boss && !this.boss.isDying) this.boss.invulnerable = false;
    this.eventBus.emit('levy:closed');
  }
}
